import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function TechnicalSection({ eventId, permissions }: { eventId: string; permissions: any }) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});

  const images = [
    { id: 1, placeholder: true },
    { id: 2, placeholder: true },
    { id: 3, placeholder: true },
    { id: 4, placeholder: true },
  ];

  const fileCategories = [
    { id: 'advance_docs', label: 'Advance Docs', count: 0 },
    { id: 'technical_drawings', label: 'Technical Drawings', count: 0 },
    { id: 'production_files', label: 'Production Files', count: 0 },
    { id: 'zip_files', label: 'Zip Files', count: 0 },
    { id: 'venue_images', label: 'Venue Images', count: 0 },
    { id: 'venue_files', label: 'Venue Files', count: 0 },
    { id: 'venue_cads', label: 'Venue CADs', count: 0 },
    { id: 'notes', label: 'Notes', count: 0 },
  ];

  const toggleSection = (sectionId: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2">
        <h2 className="text-2xl font-semibold text-white mb-6">Renderings / Images</h2>

        <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg overflow-hidden">
          <div className="relative aspect-video bg-black flex items-center justify-center">
            {images[currentImageIndex].placeholder ? (
              <div className="text-zinc-600 text-center">
                <div className="text-6xl mb-2">🎥</div>
                <p className="text-sm">Image placeholder {currentImageIndex + 1}</p>
              </div>
            ) : null}

            <button
              onClick={prevImage}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-zinc-900/80 hover:bg-zinc-900 rounded-full flex items-center justify-center text-white transition-colors"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>

            <button
              onClick={nextImage}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-zinc-900/80 hover:bg-zinc-900 rounded-full flex items-center justify-center text-white transition-colors"
            >
              <ChevronRight className="w-6 h-6" />
            </button>

            <div className="absolute top-4 left-4 bg-zinc-900/80 px-3 py-1.5 rounded text-white text-sm font-medium">
              {currentImageIndex + 1} / {images.length}
            </div>
          </div>
        </div>
      </div>

      <div className="lg:col-span-1 space-y-3">
        {fileCategories.map((category) => (
          <div key={category.id} className="bg-primary-500 hover:bg-primary-600 transition-colors rounded-lg overflow-hidden">
            <button
              onClick={() => toggleSection(category.id)}
              className="w-full px-6 py-4 flex items-center justify-between text-left"
            >
              <span className="text-white font-medium">{category.label}</span>
              <Plus
                className={`w-5 h-5 text-white transition-transform ${
                  expandedSections[category.id] ? 'rotate-45' : ''
                }`}
              />
            </button>

            {expandedSections[category.id] && (
              <div className="px-6 py-4 bg-zinc-900/50 border-t border-primary-600">
                <p className="text-zinc-400 text-sm">
                  {category.count === 0
                    ? 'No files uploaded yet'
                    : `${category.count} files`}
                </p>
                {permissions.canEdit && (
                  <Button
                    size="sm"
                    className="mt-3 bg-primary-500 hover:bg-primary-600 text-white"
                  >
                    Upload Files
                  </Button>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
